import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import { ActivityType, ViewMode } from '@/utils/activity-calendar/enums/ActivityCalendar.enum';
import { IActivity as Activity } from '@/utils/activity-calendar/interfaces/IActivity';
import { IAnchor as Anchor } from '@/utils/activity-calendar/interfaces/IAnchor';
import {
  ALL_FARMS_FILTER,
  API_VIEW_MODE,
  CALENDAR_MONTH_WINDOW_EACH_SIDE,
  DATE_FORMAT,
  DAYS_PER_WEEK,
  DAYS_TO_END_OF_WEEK,
  FIRST_DAY_OF_MONTH,
  FIRST_DAY_OF_YEAR,
  FIRST_MONTH_INDEX,
  LAST_DAY_OF_YEAR,
  LAST_MONTH_INDEX,
  MILLISECONDS_PER_DAY,
  MONTHS_FULL,
  MONTHS_PER_YEAR,
  MONTHS_SHORT,
} from '@/utils/activity-calendar/constants';
import { invalidateActivityCalendarCache, useActivityCalendar } from '@/utils/hooks/useActivityCalendar';
import { anchorToDayjs, getRecordPath, indexBy, weekStart } from '@/utils/activity-calendar/services/activityCalendarUtils';
import { CalendarHeader } from './CalendarHeader';
import { Toolbar } from './Toolbar';
import { WeekView } from './WeekView';
import { MonthView } from './MonthView';
import { YearView } from './YearView';
import { WeekViewSkeleton, MonthViewSkeleton, YearViewSkeleton } from './CalendarSkeleton';

const MOBILE_BREAKPOINT = 700;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const isViewMode = (value: string): value is ViewMode => Object.values(ViewMode).includes(value as ViewMode);

export const ActivityCalendar: FC = () => {
  const today = useMemo(() => dayjs().startOf('day'), []);
  const bounds = useMemo(() => {
    const minDate = today.add(-CALENDAR_MONTH_WINDOW_EACH_SIDE, 'month').startOf('month');
    const maxDate = today.add(CALENDAR_MONTH_WINDOW_EACH_SIDE, 'month').endOf('month');
    return {
      minDate,
      maxDate,
      minMonthKey: minDate.year() * MONTHS_PER_YEAR + minDate.month(),
      maxMonthKey: maxDate.year() * MONTHS_PER_YEAR + maxDate.month(),
      minYear: minDate.year(),
      maxYear: maxDate.year(),
    };
  }, [today]);

  const router = useRouter();

  const [view, setView] = useState<ViewMode>(ViewMode.Week);
  const [anchor, setAnchor] = useState<Anchor>({
    year: today.year(),
    monthIndex: today.month(),
    dayOfMonth: today.date(),
  });
  const [farm, setFarm] = useState<string>(ALL_FARMS_FILTER);
  const [types, setTypes] = useState<Record<ActivityType, boolean>>({
    [ActivityType.Spray]: true,
    [ActivityType.Dry]: true,
    [ActivityType.Fertigation]: true,
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const stateRestored = useRef(false);
  useEffect(() => {
    if (!router.isReady || stateRestored.current) return;
    stateRestored.current = true;
    const { calView, calYear, calMonth, calDay, calFarm } = router.query;

    if (typeof calView === 'string') {
      invalidateActivityCalendarCache();
    }

    if (typeof calView === 'string' && isViewMode(calView)) {
      setView(calView);
    }
    if (typeof calYear === 'string' && typeof calMonth === 'string' && typeof calDay === 'string') {
      const year = Number(calYear);
      const monthIndex = Number(calMonth);
      const dayOfMonth = Number(calDay);
      if (!Number.isNaN(year) && !Number.isNaN(monthIndex) && !Number.isNaN(dayOfMonth)) {
        setAnchor({ year, monthIndex, dayOfMonth });
      }
    }
    if (typeof calFarm === 'string') {
      setFarm(calFarm);
    }
  }, [router.isReady, router.query]);

  const handleActivityClick = useCallback(
    (activity: Activity) => {
      const path = getRecordPath(activity);
      if (!path) return;
      const calState = new URLSearchParams({
        calView: view,
        calYear: String(anchor.year),
        calMonth: String(anchor.monthIndex),
        calDay: String(anchor.dayOfMonth),
        calFarm: farm,
      }).toString();
      router.push(`${path}?returnTo=${encodeURIComponent(`?${calState}`)}`);
    },
    [view, anchor, farm, router],
  );

  const { fromDate, untilDate } = useMemo(() => {
    if (view === ViewMode.Week) {
      const weekStartDate = weekStart(anchorToDayjs(anchor));
      return {
        fromDate: weekStartDate.format(DATE_FORMAT),
        untilDate: weekStartDate.add(DAYS_TO_END_OF_WEEK, 'day').format(DATE_FORMAT),
      };
    }
    if (view === ViewMode.Month) {
      const monthDate = dayjs(new Date(anchor.year, anchor.monthIndex, FIRST_DAY_OF_MONTH));
      return {
        fromDate: monthDate.startOf('month').format(DATE_FORMAT),
        untilDate: monthDate.endOf('month').format(DATE_FORMAT),
      };
    }
    const yearStart = dayjs(new Date(anchor.year, FIRST_MONTH_INDEX, FIRST_DAY_OF_YEAR));
    const yearEnd = dayjs(new Date(anchor.year, LAST_MONTH_INDEX, LAST_DAY_OF_YEAR));
    return {
      fromDate: yearStart.format(DATE_FORMAT),
      untilDate: yearEnd.format(DATE_FORMAT),
    };
  }, [view, anchor]);

  const viewMode = API_VIEW_MODE[view];
  const { activities, farms: apiFarms, loading, error } = useActivityCalendar(fromDate, untilDate, viewMode);

  const visible = useMemo(
    () => activities.filter((a) => (farm === ALL_FARMS_FILTER || a.farm === farm) && types[a.type]),
    [activities, farm, types],
  );
  const idx = useMemo(() => indexBy(visible), [visible]);

  const shift = (dir: 1 | -1) => {
    if (view === ViewMode.Month) {
      const monthKey = clamp(
        anchor.year * MONTHS_PER_YEAR + anchor.monthIndex + dir,
        bounds.minMonthKey,
        bounds.maxMonthKey,
      );
      setAnchor({
        year: Math.floor(monthKey / MONTHS_PER_YEAR),
        monthIndex: ((monthKey % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR,
        dayOfMonth: FIRST_DAY_OF_MONTH,
      });
    } else if (view === ViewMode.Year) {
      setAnchor({ ...anchor, year: clamp(anchor.year + dir, bounds.minYear, bounds.maxYear) });
    } else {
      let date = anchorToDayjs(anchor).add(dir * DAYS_PER_WEEK, 'day');
      if (date.isBefore(bounds.minDate)) date = bounds.minDate;
      if (date.isAfter(bounds.maxDate)) date = bounds.maxDate;
      setAnchor({ year: date.year(), monthIndex: date.month(), dayOfMonth: date.date() });
    }
  };

  const goToday = () => setAnchor({ year: today.year(), monthIndex: today.month(), dayOfMonth: today.date() });
  const openMonth = (year: number, monthIndex: number) => {
    setView(ViewMode.Month);
    setAnchor({ year, monthIndex, dayOfMonth: FIRST_DAY_OF_MONTH });
  };

  let prevDisabled = false;
  let nextDisabled = false;
  if (view === ViewMode.Month) {
    const monthKey = anchor.year * MONTHS_PER_YEAR + anchor.monthIndex;
    prevDisabled = monthKey <= bounds.minMonthKey;
    nextDisabled = monthKey >= bounds.maxMonthKey;
  } else if (view === ViewMode.Year) {
    prevDisabled = anchor.year <= bounds.minYear;
    nextDisabled = anchor.year >= bounds.maxYear;
  } else {
    const weekStartTimestamp = weekStart(anchorToDayjs(anchor)).valueOf();
    prevDisabled = weekStartTimestamp <= weekStart(bounds.minDate).valueOf();
    nextDisabled = weekStartTimestamp >= weekStart(bounds.maxDate).valueOf();
  }

  const periodLabel = useMemo(() => {
    if (view === ViewMode.Month) return `${MONTHS_FULL[anchor.monthIndex]} ${anchor.year}`;
    if (view === ViewMode.Year) return String(anchor.year);
    const startDate = weekStart(anchorToDayjs(anchor));
    const endDate = startDate.add(DAYS_TO_END_OF_WEEK, 'day');
    if (startDate.month() === endDate.month()) {
      return `${startDate.date()} – ${endDate.date()} ${MONTHS_SHORT[endDate.month()]} ${endDate.year()}`;
    }
    return `${startDate.date()} ${MONTHS_SHORT[startDate.month()]} – ${endDate.date()} ${MONTHS_SHORT[endDate.month()]} ${endDate.year()}`;
  }, [view, anchor]);

  const counts = useMemo(() => {
    let inPeriod: (activity: Pick<Activity, 'year' | 'monthIndex' | 'timestamp'>) => boolean;
    if (view === ViewMode.Month) {
      inPeriod = (activity) => activity.year === anchor.year && activity.monthIndex === anchor.monthIndex;
    } else if (view === ViewMode.Year) {
      inPeriod = (activity) => activity.year === anchor.year;
    } else {
      const startTimestamp = weekStart(anchorToDayjs(anchor)).valueOf();
      const endTimestamp = startTimestamp + DAYS_PER_WEEK * MILLISECONDS_PER_DAY;
      inPeriod = (activity) => activity.timestamp >= startTimestamp && activity.timestamp < endTimestamp;
    }
    const result: Record<ActivityType, number> = {
      [ActivityType.Spray]: 0,
      [ActivityType.Dry]: 0,
      [ActivityType.Fertigation]: 0,
    };
    visible.filter(inPeriod).forEach((a) => {
      result[a.type] += 1;
    });
    return result;
  }, [view, anchor, visible]);

  const rangeLabel = `${MONTHS_SHORT[bounds.minDate.month()]} ${bounds.minYear} – ${MONTHS_SHORT[bounds.maxDate.month()]} ${bounds.maxYear}`;
  const subtitle = `${visible.length} activities across the period · ${rangeLabel}`;

  const toggleType = (type: ActivityType) => setTypes((s) => ({ ...s, [type]: !s[type] }));

  return (
    <section className="w-full overflow-hidden rounded-[8px] border bg-white" style={{ borderColor: '#E5E7EB' }}>
      <CalendarHeader
        subtitle={subtitle}
        farm={farm}
        farms={apiFarms}
        onFarmChange={setFarm}
        view={view}
        onViewChange={setView}
      />
      <Toolbar
        periodLabel={periodLabel}
        prevDisabled={prevDisabled}
        nextDisabled={nextDisabled}
        onPrev={() => !prevDisabled && shift(-1)}
        onNext={() => !nextDisabled && shift(1)}
        onToday={goToday}
        types={types}
        counts={counts}
        onToggleType={toggleType}
      />

      <div className="p-[18px]">
        {error && !loading && (
          <div className="flex items-center justify-center py-[32px] text-[13px] text-red-500">{error}</div>
        )}
        {loading && (
          <>
            {view === ViewMode.Week && <WeekViewSkeleton isMobile={isMobile} />}
            {view === ViewMode.Month && <MonthViewSkeleton />}
            {view === ViewMode.Year && <YearViewSkeleton />}
          </>
        )}
        {!loading && !error && (
          <>
            {view === ViewMode.Week && <WeekView idx={idx} anchor={anchor} today={today} isMobile={isMobile} onActivityClick={handleActivityClick} />}
            {view === ViewMode.Month && <MonthView idx={idx} anchor={anchor} today={today} onActivityClick={handleActivityClick} />}
            {view === ViewMode.Year && (
              <YearView
                idx={idx}
                year={anchor.year}
                today={today}
                minMonthKey={bounds.minMonthKey}
                maxMonthKey={bounds.maxMonthKey}
                onOpenMonth={openMonth}
              />
            )}
          </>
        )}
      </div>

    </section>
  );
};

export default ActivityCalendar;
